import React, { useState } from 'react';
import { View, TouchableOpacity, StyleSheet, Modal, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

export interface JobFilterOptions {
  budgetRange: 'all' | 'under-1000' | '1000-5000' | 'over-5000';
  category: string | 'all';
  timeframe: 'all' | 'today' | 'week' | 'month';
}

interface JobFiltersProps {
  filters: JobFilterOptions;
  onFiltersChange: (filters: JobFilterOptions) => void;
  categories: string[];
}

const JobFilters: React.FC<JobFiltersProps> = ({ filters, onFiltersChange, categories }) => {
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const [showModal, setShowModal] = useState(false);
  const [tempFilters, setTempFilters] = useState<JobFilterOptions>(filters);

  const activeFilterCount = [
    filters.budgetRange !== 'all',
    filters.category !== 'all',
    filters.timeframe !== 'all',
  ].filter(Boolean).length;

  const handleApply = () => {
    onFiltersChange(tempFilters);
    setShowModal(false);
  };

  const handleReset = () => {
    const resetFilters: JobFilterOptions = {
      budgetRange: 'all',
      category: 'all',
      timeframe: 'all',
    };
    setTempFilters(resetFilters);
    onFiltersChange(resetFilters);
    setShowModal(false);
  };

  return (
    <>
      <TouchableOpacity
        style={[styles.filterButton, { backgroundColor: theme.backgroundSecondary }]}
        onPress={() => setShowModal(true)}
        activeOpacity={0.7}
      >
        <Ionicons name="funnel-outline" size={20} color={theme.textSecondary} />
        {activeFilterCount > 0 && (
          <View style={[styles.badge, { backgroundColor: theme.accent }]}>
            <ThemedText style={[styles.badgeText, { color: '#000000' }]}>{activeFilterCount}</ThemedText>
          </View>
        )}
      </TouchableOpacity>

      <Modal
        visible={showModal}
        animationType="slide"
        transparent={true}
        statusBarTranslucent={true}
        onRequestClose={() => setShowModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.backgroundSecondary }]}>
            <View style={[styles.modalHeader, { borderBottomColor: theme.border }]}>
              <ThemedText style={[styles.modalTitle, { color: theme.text }]}>Filter Jobs</ThemedText>
              <TouchableOpacity onPress={() => setShowModal(false)}>
                <Ionicons name="close" size={24} color={theme.textSecondary} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
              {/* Budget Range */}
              <View style={styles.filterSection}>
                <ThemedText style={[styles.filterLabel, { color: theme.text }]}>Budget Range</ThemedText>
                <View style={styles.filterOptionsRow}>
                  {[
                    { label: 'All Budgets', value: 'all' },
                    { label: 'Under $1,000', value: 'under-1000' },
                    { label: '$1,000 - $5,000', value: '1000-5000' },
                    { label: 'Over $5,000', value: 'over-5000' },
                  ].map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      style={[
                        styles.filterChip,
                        {
                          backgroundColor: tempFilters.budgetRange === option.value ? theme.accent : theme.backgroundTertiary,
                          borderColor: tempFilters.budgetRange === option.value ? theme.accent : theme.border,
                        },
                      ]}
                      onPress={() => setTempFilters({ ...tempFilters, budgetRange: option.value as any })}
                      activeOpacity={0.7}
                    >
                      <ThemedText
                        style={[
                          styles.filterChipText,
                          { color: tempFilters.budgetRange === option.value ? theme.textInverse : theme.text },
                        ]}
                      >
                        {option.label}
                      </ThemedText>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Category */}
              <View style={styles.filterSection}>
                <ThemedText style={[styles.filterLabel, { color: theme.text }]}>Category</ThemedText>
                <View style={styles.filterOptionsRow}>
                  <TouchableOpacity
                    style={[
                      styles.filterChip,
                      {
                        backgroundColor: tempFilters.category === 'all' ? theme.accent : theme.backgroundTertiary,
                        borderColor: tempFilters.category === 'all' ? theme.accent : theme.border,
                      },
                    ]}
                    onPress={() => setTempFilters({ ...tempFilters, category: 'all' })}
                    activeOpacity={0.7}
                  >
                    <ThemedText
                      style={[
                        styles.filterChipText,
                        { color: tempFilters.category === 'all' ? theme.textInverse : theme.text },
                      ]}
                    >
                      All Categories
                    </ThemedText>
                  </TouchableOpacity>
                  {categories.map((cat) => (
                    <TouchableOpacity
                      key={cat}
                      style={[
                        styles.filterChip,
                        {
                          backgroundColor: tempFilters.category === cat ? theme.accent : theme.backgroundTertiary,
                          borderColor: tempFilters.category === cat ? theme.accent : theme.border,
                        },
                      ]}
                      onPress={() => setTempFilters({ ...tempFilters, category: cat })}
                      activeOpacity={0.7}
                    >
                      <ThemedText
                        style={[
                          styles.filterChipText,
                          { color: tempFilters.category === cat ? theme.textInverse : theme.text },
                        ]}
                      >
                        {cat}
                      </ThemedText>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Timeframe */}
              <View style={styles.filterSection}>
                <ThemedText style={[styles.filterLabel, { color: theme.text }]}>Posted</ThemedText>
                <View style={styles.filterOptionsRow}>
                  {[
                    { label: 'All Time', value: 'all' },
                    { label: 'Today', value: 'today' },
                    { label: 'This Week', value: 'week' },
                    { label: 'This Month', value: 'month' },
                  ].map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      style={[
                        styles.filterChip,
                        {
                          backgroundColor: tempFilters.timeframe === option.value ? theme.accent : theme.backgroundTertiary,
                          borderColor: tempFilters.timeframe === option.value ? theme.accent : theme.border,
                        },
                      ]}
                      onPress={() => setTempFilters({ ...tempFilters, timeframe: option.value as any })}
                      activeOpacity={0.7}
                    >
                      <ThemedText
                        style={[
                          styles.filterChipText,
                          { color: tempFilters.timeframe === option.value ? theme.textInverse : theme.text },
                        ]}
                      >
                        {option.label}
                      </ThemedText>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </ScrollView>

            <View style={[styles.modalFooter, { borderTopColor: theme.border, paddingBottom: insets.bottom + 16 }]}>
              <TouchableOpacity
                style={[styles.resetButton, { borderColor: theme.border }]}
                onPress={handleReset}
                activeOpacity={0.7}
              >
                <ThemedText style={[styles.resetButtonText, { color: theme.textSecondary }]}>Reset</ThemedText>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.applyButton, { backgroundColor: theme.accent }]}
                onPress={handleApply}
                activeOpacity={0.7}
              >
                <ThemedText style={[styles.applyButtonText, { color: theme.textInverse }]}>Apply Filters</ThemedText>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  badge: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '700',
  },
  modalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  modalBody: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  filterSection: {
    marginBottom: 24,
  },
  filterLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  filterOptionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterChip: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1,
  },
  filterChipText: {
    fontSize: 14,
    fontWeight: '600',
  },
  modalFooter: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    gap: 12,
  },
  resetButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  resetButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  applyButton: {
    flex: 2,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  applyButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default JobFilters;
